import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

export interface HorizonOptionItem {
  id: string;
  label: string;
  value: number | string;
}

export function parseHorizonOptions(options?: any): HorizonOptionItem[] {
  const rawList = options?.options || ['1-Day', '3-Day', '5-Day', 'Max'];
  return rawList.map((item: any) => {
    if (typeof item === 'object' && item !== null && item.label) {
      return {
        id: String(item.id || item.value || item.label),
        label: String(item.label),
        value: item.value !== undefined ? item.value : item.id
      };
    }
    const str = String(item).trim();
    const lower = str.toLowerCase();
    if (lower === '1' || lower === '1-day' || lower === '1d' || lower === '1 day') {
      return { id: '1', label: '1-Day', value: 1 };
    }
    if (lower === '3' || lower === '3-day' || lower === '3d' || lower === '3 day') {
      return { id: '3', label: '3-Day', value: 3 };
    }
    if (lower === '5' || lower === '5-day' || lower === '5d' || lower === '5 day') {
      return { id: '5', label: '5-Day', value: 5 };
    }
    if (lower === 'max' || lower === 'all') {
      return { id: 'max', label: 'Max', value: 'max' };
    }
    // Custom number of days
    const numMatch = str.match(/^(\d+)/);
    if (numMatch) {
      const n = parseInt(numMatch[1], 10);
      return { id: String(n), label: str.includes('Day') ? str : `${n}-Day`, value: n };
    }
    return { id: str, label: str, value: str };
  });
}

export class HorizonSelectorWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title;
    const options = parseHorizonOptions(widget.options);
    const activeVal = String(ctx.viewDays).toLowerCase();

    const buttonsHtml = options.map(opt => {
      const isSelected = String(opt.value).toLowerCase() === activeVal || opt.id.toLowerCase() === activeVal;
      const btnStyle = isSelected
        ? 'background: #3b82f6; color: #ffffff; font-weight: 700;'
        : 'background: transparent; color: #94a3b8; font-weight: 500;';

      return `
        <button 
          class="horizon-btn" 
          data-horizon="${opt.value}" 
          data-horizon-id="${opt.id}"
          style="padding: 5px 14px; font-size: 0.78rem; border-radius: 4px; border: none; cursor: pointer; transition: all 0.2s ease; ${btnStyle}"
        >
          ${opt.label}
        </button>
      `;
    }).join('');

    return `
      <div id="widget-${widget.id}" class="horizon-selector-widget" style="background: rgba(255,255,255,0.03); border: 1px solid var(--border-light); border-radius: 8px; padding: 10px 14px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;">
        ${title ? `
          <div style="font-size: 0.82rem; font-weight: 600; color: #94a3b8; display: flex; align-items: center; gap: 6px;">
            <span>⏱️</span>
            <span>${title}</span>
          </div>
        ` : '<div></div>'}
        <div style="display: inline-flex; background: rgba(0,0,0,0.35); padding: 3px; border-radius: 6px; border: 1px solid var(--border-light); gap: 2px;">
          ${buttonsHtml}
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const container = document.getElementById(`widget-${widget.id}`);
    if (!container) return;

    const buttons = container.querySelectorAll<HTMLButtonElement>('.horizon-btn');
    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const rawVal = btn.getAttribute('data-horizon');
        if (!rawVal) return;

        let val: number | string = rawVal;
        if (!isNaN(Number(rawVal)) && rawVal !== 'max') {
          val = parseInt(rawVal, 10);
        }

        if (ctx.onHorizonChange) {
          ctx.onHorizonChange(val);
        }
      });
    });
  }
}
